import * as React from "react";
import { Button } from "@/components/atoms/Button";
import { Text } from "@/components/atoms/Text";
import {
  alertDismissButtonVariants,
  AlertVariantProps,
  alertVariants,
} from "@/components/molecules/Alert/alert-variants";
import { DelayedUnmount } from "@/components/utils/DelayedUnmount";
import XIcon from "@/icons/__generated__/X/X20";
import { useResizeObserver } from "@/lib/hooks/use-resize-observer/use-resize-observer";
import { cn } from "@/lib/utils";

type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
  onDismiss?: () => void;
  onUnmount?: () => void;
  fadeOnDismiss?: boolean;
  collapseOnDismiss?: boolean;
  isVisible?: boolean;
} & AlertVariantProps;

const AlertContent = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      className,
      appearance = "purple",
      iconPlacement = "top",
      isDismissable = true,
      flush = false,
      onDismiss,
      fadeOnDismiss = true,
      collapseOnDismiss = true,
      isVisible = true,
      children,
      ...props
    },
    ref,
  ) => {
    const [heightRef, { domRect }] =
      useResizeObserver<HTMLDivElement>(collapseOnDismiss);
    const { height = 0 } = domRect || {};

    const [currentHeight, setCurrentHeight] = React.useState<
      number | undefined
    >(undefined);

    const heightWithMargins = () => {
      const computedStyles = getComputedStyle(heightRef.current as HTMLElement);

      const marginBottom = parseInt(
        computedStyles.marginBottom?.replace("px", "") || "0",
      );
      const marginTop = parseInt(
        computedStyles.marginTop?.replace("px", "") || "0",
      );

      return height + marginBottom + marginTop;
    };

    // Collapse animation behavior
    React.useLayoutEffect(() => {
      if (!collapseOnDismiss) {
        return;
      }

      if (isVisible) {
        if (height > 0) {
          // Cache visible height to animate collapse on dismiss
          setCurrentHeight(heightWithMargins());
        }
      } else {
        // Collapse to 0 when hidden
        setCurrentHeight(0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [height, collapseOnDismiss, isVisible]);

    return (
      <div
        ref={ref}
        role="alert"
        data-state={isVisible ? "visible" : "hidden"}
        style={{ height: collapseOnDismiss ? `${currentHeight}px` : "auto" }}
        className={cn("tw-w-full tw-fill-mode-forwards", {
          "data-[state=hidden]:tw-animate-out data-[state=hidden]:tw-fade-out-0 !tw-duration-300":
            fadeOnDismiss,
          "tw-transition-all tw-overflow-hidden tw-duration-300 tw-mb-0":
            collapseOnDismiss,
        })}
        {...props}
      >
        <div
          ref={heightRef}
          className={cn(
            alertVariants({ appearance, isDismissable, iconPlacement, flush }),
            className,
          )}
        >
          {children}
          {isDismissable && (
            <Button
              variant="text-only"
              className={cn(alertDismissButtonVariants({ appearance }))}
              onClick={onDismiss}
            >
              <XIcon />
            </Button>
          )}
        </div>
      </div>
    );
  },
);
AlertContent.displayName = "AlertContent";

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    {
      isVisible = true,
      fadeOnDismiss = true,
      collapseOnDismiss = true,
      onUnmount,
      ...props
    },
    ref,
  ) => {
    return fadeOnDismiss || collapseOnDismiss ? (
      // Delay unmount for exit animations
      <DelayedUnmount isMounted={isVisible} onUnmountChildren={onUnmount}>
        <AlertContent
          ref={ref}
          isVisible={isVisible}
          fadeOnDismiss={fadeOnDismiss}
          collapseOnDismiss={collapseOnDismiss}
          {...props}
        />
      </DelayedUnmount>
    ) : (
      isVisible && (
        <AlertContent
          ref={ref}
          fadeOnDismiss={fadeOnDismiss}
          collapseOnDismiss={collapseOnDismiss}
          {...props}
        />
      )
    );
  },
);
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <Text
    element="h5"
    typeface="body-1-m"
    testId="alert-title"
    data-slot="alert-title"
    ref={ref}
    className={className}
    {...props}
  />
));
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <Text
    element="p"
    typeface="body-1"
    testId="alert-description"
    data-slot="alert-description"
    ref={ref}
    className={className}
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertDescription, AlertTitle, type AlertProps };
